import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
} from "react";
import {
    useGetCharacterChangeLogs,
    useGetCharacterData,
    useUpdateCharacterData,
} from "../../../../net/CharacterManagement";
import { useInterval } from "../../../../common/hooks/IntervalHook";
import { CharacterDataJSON } from "../../../../utils/game/character/CharacterDataJSON";
import CharacterSheet from "./CharacterSheet";
import { CharacterDataUtils } from "../../../../utils/game/character/CharacterData";
import ActiveUserContext from "../../../../utils/session/ActiveUserContext";
import FlexRow from "../../../../common/components/layout/Flexbox/FlexRow";
import Cookies from "universal-cookie";
import { Dictionary } from "../../../../utils/sys/Dictionary";
import { WhitelistUtils } from "../../../../utils/sys/Whitelist";
import Spinner from "../../../../common/components/system/Spinner/Spinner";

import "./CharacterSheet_style.css";
import { KeyValuePair } from "../../../../utils/sys/KeyValuePair";
import { useDocumentTitle } from "../../../../common/utilities/DocumentTitle";

type Props = {
    characterID: number;
    applyDocumentTitle?: boolean;
};

export default function CharacterSheetContainer({
    characterID,
    applyDocumentTitle,
}: Props): JSX.Element {
    const activeUser = useContext(ActiveUserContext);
    const rawCharacterData = useRef<CharacterDataJSON | null>(null);
    const [loadCharacterData, isLoadingCharacterData] = useGetCharacterData();
    const [updateCharacterData, isUpdatingCharacterData] =
        useUpdateCharacterData();
    const [loadCharacterChangeLogs, isLoadingCharacterChangeLogs] = useGetCharacterChangeLogs();
    const lastChangeLogTimestamp = useRef<number>(Math.floor(Date.now() / 1000));
    const cookies = new Cookies();

    useInterval(() => {
        if (characterID == null || characterID < 0) {
            return;
        }
        const startTimestamp = lastChangeLogTimestamp.current;
        lastChangeLogTimestamp.current = Math.floor(Date.now() / 1000);
        loadCharacterChangeLogs({
            variables: {
                characterID: characterID,
                startTimestamp: startTimestamp,
                token: cookies.get("session_token"),
            },
            onComplete: (result) => {
                if (
                    result == null ||
                    result.length === 0 ||
                    rawCharacterData.current == null
                ) {
                    return;
                }

                const applicableLogs = [...result].sort(
                    (a, b) => (a.edit_timestamp ?? 0) - (b.edit_timestamp ?? 0)
                );

                // Later logs take precedence, so earlier changes to the same
                // field are superseded before merging.
                let consolidatedChanges: CharacterDataJSON = {};
                applicableLogs.forEach((log) => {
                    if (!log.changes) {
                        return;
                    }
                    try {
                        const parsedChanges = JSON.parse(
                            log.changes
                        ) as CharacterDataJSON;
                        consolidatedChanges = {
                            ...consolidatedChanges,
                            ...parsedChanges,
                        };
                    } catch (error) {
                        console.log(error);
                    }
                });

                const merged = CharacterDataUtils.mergeIn(
                    rawCharacterData.current,
                    consolidatedChanges
                );
                if (merged !== false) {
                    rawCharacterData.current = merged;
                }
            },
            onError: (error) => {
                console.log(error);
            },
        });
    }, 60000);

    useEffect(() => {
        if (
            characterID != null &&
            characterID !== rawCharacterData.current?.id
        ) {
            lastChangeLogTimestamp.current = Math.floor(Date.now() / 1000);
            loadCharacterData({
                variables: {
                    id: characterID,
                    token: cookies.get("session_token"),
                },
                onComplete: (result) => {
                    rawCharacterData.current = result;
                },
                onError: (error) => {
                    rawCharacterData.current = null;
                },
            });
        }
    }, [characterID]);

    const characterData = useMemo(() => {
        return rawCharacterData.current != null
            ? CharacterDataUtils.processRawCharacterData(
                  rawCharacterData.current
              )
            : null;
    }, [rawCharacterData.current]);

    useDocumentTitle(applyDocumentTitle ? characterData?.name : undefined);

    const editable = useMemo(() => {
        const wl = {
            viewers: rawCharacterData.current?.whitelist?.readers ?? [],
            editors: rawCharacterData.current?.whitelist?.writers ?? [],
        };
        const useWL = rawCharacterData.current?.whitelist_protected === true;
        const creator = rawCharacterData.current?.creator ?? -1;

        return activeUser.id <= 0
            ? false
            : characterData == null
            ? true
            : !useWL ||
              characterData.creator === creator ||
              WhitelistUtils.canEdit(activeUser.id, wl);
    }, [
        rawCharacterData,
        activeUser.id,
        characterData,
    ]);

    const handleCharacterDataChange = useCallback(
        (vals: Dictionary | Array<KeyValuePair>) => {
            const newData = CharacterDataUtils.valueArrayToRawCharacterData(
                Array.isArray(vals) ? vals : vals.toArray()
            );
            const keys = Object.keys(newData);
            if (keys.length > 0) {
                updateCharacterData({
                    variables: {
                        id: characterID,
                        token: cookies.get("session_token"),
                        data: newData,
                    },
                    onComplete: (response) => {
                        if (
                            rawCharacterData.current != null &&
                            response != null
                        ) {
                            const merge = CharacterDataUtils.mergeIn(
                                rawCharacterData.current,
                                response
                            );
                            if (merge !== false) {
                                rawCharacterData.current = merge;
                            }
                        }
                    },
                    onError: (error) => {
                        console.log(error);
                    },
                });
            }
        },
        [rawCharacterData, characterData]
    );

    return (
        <div className="character-sheet-container">
            {isLoadingCharacterData() && (
                <FlexRow style={{ marginTop: 16, marginBottom: 16 }}>
                    <Spinner />
                </FlexRow>
            )}
            {!isLoadingCharacterData() && characterData == null && (
                <div>Failed to Load</div>
            )}
            {!isLoadingCharacterData() &&
                rawCharacterData != null &&
                characterData != null && (
                    <CharacterSheet
                        characterData={characterData}
                        isEditable={editable}
                        isRefreshing={
                            isLoadingCharacterData() ||
                            isUpdatingCharacterData() || 
                            isLoadingCharacterChangeLogs()
                        }
                        onCharacterDataValueChanged={(vals) => {
                            handleCharacterDataChange(vals);
                        }}
                    />
                )}
        </div>
    );
}
